import React, { useCallback, useMemo, useRef, useState } from "react";
import ConfirmDialog from "../common/ConfirmDialog";
import { UnsavedChangesContext } from "../../hooks/useUnsavedChangesGuard";

// Registry voor niet-opgeslagen wijzigingen + de bevestiging vóór shell-navigatie.
// Zie hooks/useUnsavedChangesGuard.js voor het waarom (BrowserRouter, geen useBlocker).
export default function UnsavedChangesProvider({ children }) {
  // Set van id's die op dit moment wijzigingen hebben. Een ref: aan- en afmelden hoeft
  // niets te hertekenen, alleen de vraag bij het navigeren leest hem.
  const dirtyIds = useRef(new Set());
  const [pending, setPending] = useState(null); // { proceed } | null

  const setDirty = useCallback((id, dirty) => {
    if (dirty) dirtyIds.current.add(id);
    else dirtyIds.current.delete(id);
  }, []);

  const isDirty = useCallback(() => dirtyIds.current.size > 0, []);

  const requestNavigation = useCallback((proceed) => {
    if (dirtyIds.current.size === 0) {
      proceed();
      return;
    }
    setPending({ proceed });
  }, []);

  const handleConfirm = useCallback(() => {
    const next = pending;
    setPending(null);
    // De editor ontkoppelt bij het wisselen van pagina en meldt zich dan zelf af; tot dan
    // mag deze ene navigatie niet opnieuw geblokkeerd worden.
    dirtyIds.current.clear();
    next?.proceed();
  }, [pending]);

  const handleCancel = useCallback(() => setPending(null), []);

  const value = useMemo(() => ({ setDirty, isDirty, requestNavigation }), [setDirty, isDirty, requestNavigation]);

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={!!pending}
        title="Wijzigingen weggooien?"
        message="Je hebt wijzigingen die nog niet opgeslagen zijn. Als je deze pagina verlaat, gaan ze verloren."
        confirmLabel="Weggooien"
        tone="danger"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </UnsavedChangesContext.Provider>
  );
}
